// pkg158 — dropdown follows catalog order with a dynamic default (first template preselects once the catalog loads, replacing the hardcoded discovery_tools) and the stale two-template subtitle becomes template-count-agnostic — delivered 2026-08-18
// pkg153 — Onboarding Emails as its own HQ page (D-153a): the pkg151 tool moves out of the AdminHQPage card into /admin/dashboard/hq/emails with its own left-nav link on the HQ shell; same three adminApi methods, always-expanded layout, taller preview — delivered 2026-08-18
import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';

// Onboarding Emails — LawMighty HQ.
//
// Operator-sent onboarding templates (platform identity, mail@lawmighty.com).
// Extracted from the AdminHQPage card (pkg151/151a) so the tool has its own
// page instead of sitting under the firm list. Backend endpoints unchanged
// (routes/hq.js pkg151): owner + HQ-firm enforced server-side; every send is
// activity-logged as 'hq_onboarding_email'. If a send fails, Preview is the
// manual fallback — copy the rendered email and send from the M365 mailbox.

const font = { fontFamily: 'Arial, sans-serif' };
const NAVY_DEEP = '#0F1838';

export default function AdminHQOnboardingEmailsPage() {
  const [templates, setTemplates] = useState([]);
  const [ob, setOb] = useState({ template: '', to: '', first_name: '' }); // pkg158 — default set from the catalog on load
  const [preview, setPreview] = useState(null); // { subject, html } | null
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(() => {
    adminApi.hqListOnboardingTemplates()
      .then(r => {
        const list = r.templates || [];
        setTemplates(list); setLoading(false);
        setOb(p => (p.template ? p : { ...p, template: (list[0] || {}).key || '' })); // pkg158
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(key, fn, ok) {
    setBusy(key); setError(''); setNotice('');
    try { const r = await fn(); setNotice(ok(r)); }
    catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  const selected = templates.find(t => t.key === ob.template) || null;

  const doPreview = () => act('preview',
    () => adminApi.hqPreviewOnboardingEmail(ob.template, ob.first_name).then(r => { setPreview(r); return r; }),
    r => 'Preview updated — "' + r.subject + '"');

  const doSend = () => {
    const label = selected ? selected.label : ob.template;
    if (!window.confirm('Send "' + label + '" to ' + ob.to + '?')) return;
    act('send',
      () => adminApi.hqSendOnboardingEmail({ to: ob.to, template: ob.template, first_name: ob.first_name }),
      r => 'Sent "' + r.subject + '" to ' + r.sent_to + '.');
  };

  if (loading) return <div style={{ padding: 40, ...font, color: '#888' }}>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Onboarding Emails</h1>
      <p className="page-subtitle">
        Send a new firm one of the onboarding emails below — the list runs in the order you'd typically send them. Emails go out
        from the platform address (mail@lawmighty.com) and every send is recorded in the activity log.
      </p>

      {error  && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="card">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ ...font, fontSize: 12, color: '#444' }}>Template<br />
            <select value={ob.template} onChange={e => { setOb(p => ({ ...p, template: e.target.value })); setPreview(null); }}
              style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4 }}>
              {templates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label style={{ ...font, fontSize: 12, color: '#444' }}>Recipient email<br />
            <input type="email" value={ob.to} onChange={e => setOb(p => ({ ...p, to: e.target.value }))}
              placeholder="attorney@firm.com" style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4, width: 240 }} />
          </label>
          <label style={{ ...font, fontSize: 12, color: '#444' }}>First name (for &ldquo;Hi &hellip;,&rdquo;)<br />
            <input type="text" value={ob.first_name} onChange={e => setOb(p => ({ ...p, first_name: e.target.value }))}
              style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4, width: 150 }} />
          </label>
          <button className="btn btn-sm" disabled={busy === 'preview'} onClick={doPreview}>
            {busy === 'preview' ? 'Rendering…' : 'Preview'}
          </button>
          <button className="btn btn-sm btn-primary" disabled={busy === 'send' || !ob.to.trim()} onClick={doSend}>
            {busy === 'send' ? 'Sending…' : 'Send'}
          </button>
        </div>

        {selected && (
          <div style={{ ...font, fontSize: 12, color: '#888', marginTop: 8 }}>
            Subject: &ldquo;{selected.subject}&rdquo; &middot; {selected.description}
          </div>
        )}
      </div>

      {preview && (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ ...font, fontSize: 13, fontWeight: 700, color: NAVY_DEEP, marginBottom: 8 }}>
            Preview — &ldquo;{preview.subject}&rdquo;
          </div>
          <iframe title="Onboarding email preview" srcDoc={preview.html}
            style={{ width: '100%', height: 640, border: '1px solid #ddd', borderRadius: 4, background: '#fff' }} />
        </div>
      )}

      <p style={{ ...font, fontSize: 11, color: '#999', marginTop: 10 }}>
        If a send fails, use Preview to copy the rendered email and send it manually from the M365 mailbox.
      </p>
    </div>
  );
}
